import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ShoppingCart,
  User,
  Menu,
  X,
  Coffee,
  ChevronDown,
  LogOut,
  Settings,
  Package,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import "./Navbar.css";

const categories = [
  { label: "Espresso", path: "/shop/espresso" },
  { label: "Filter Coffee", path: "/shop/filter" },
  { label: "Cold Brew", path: "/shop/cold-brew" },
  { label: "Capsules", path: "/shop/capsule" },
  { label: "Accessories", path: "/shop/accessories" },
  { label: "Merchandise", path: "/shop/merchandise" },
];

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { itemCount, setIsOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    setShopMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar-inner container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <Coffee size={26} />
          <span>
            Brew <span className="amp">&</span> Co
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="navbar-links">
          <Link to="/" className="nav-link">
            Home
          </Link>
          <div
            className="nav-dropdown"
            onMouseEnter={() => setShopMenuOpen(true)}
            onMouseMove={() => setShopMenuOpen(true)}
            onMouseDown={() => setShopMenuOpen(true)}
            onMouseUp={() => setShopMenuOpen(true)}
            onMouseLeave={() => setShopMenuOpen(false)}
          >
            <Link to="/shop" className="nav-link">
              Shop <ChevronDown size={14} />
            </Link>
            {shopMenuOpen && (
              <div className="dropdown-menu">
                <Link to="/shop" className="dropdown-item">
                  All Products
                </Link>
                <div className="dropdown-divider" />
                {categories.map((c) => (
                  <Link key={c.path} to={c.path} className="dropdown-item">
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="navbar-actions">
          {/* Cart */}
          <button
            className="cart-btn"
            onClick={() => setIsOpen(true)}
            aria-label="Open cart"
          >
            <ShoppingCart size={22} />
            {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
          </button>

          {/* User */}
          {user ? (
            <div
              className="user-dropdown"
              onMouseEnter={() => setUserMenuOpen(true)}
              onMouseMove={() => setUserMenuOpen(true)}
              onMouseDown={() => setUserMenuOpen(true)}
              onMouseUp={() => setUserMenuOpen(true)}
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              <button className="user-btn">
                <User size={20} />
                <span className="user-name">{user.name.split(" ")[0]}</span>
                <ChevronDown size={14} />
              </button>
              {userMenuOpen && (
                <div className="dropdown-menu user-menu">
                  <div className="dropdown-header">
                    {user.name}
                    <span>{user.email}</span>
                  </div>
                  <div className="dropdown-divider" />
                  <Link to="/profile" className="dropdown-item">
                    <User size={15} /> Profile
                  </Link>
                  <Link to="/orders" className="dropdown-item">
                    <Package size={15} /> My Orders
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="dropdown-item admin-link">
                      <Settings size={15} /> Admin Panel
                    </Link>
                  )}
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item logout-item"
                    onClick={handleLogout}
                  >
                    <LogOut size={15} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-links">
              <Link to="/login" className="nav-link">
                Sign In
              </Link>
              <Link
                to="/register"
                className="btn-gold"
                style={{
                  padding: "8px 18px",
                  fontSize: "14px",
                  borderRadius: "6px",
                }}
              >
                Register
              </Link>
            </div>
          )}

          {/* Mobile toggle */}
          <button
            className="mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          <Link to="/" className="mobile-link">
            Home
          </Link>
          <Link to="/shop" className="mobile-link">
            All Products
          </Link>
          {categories.map((c) => (
            <Link key={c.path} to={c.path} className="mobile-link mobile-sub">
              {c.label}
            </Link>
          ))}
          {user ? (
            <>
              <div className="mobile-divider" />
              <Link to="/profile" className="mobile-link">
                Profile
              </Link>
              <Link to="/orders" className="mobile-link">
                My Orders
              </Link>
              {isAdmin && (
                <Link to="/admin" className="mobile-link">
                  Admin Panel
                </Link>
              )}
              <button className="mobile-link logout-btn" onClick={handleLogout}>
                Sign Out
              </button>
            </>
          ) : (
            <>
              <div className="mobile-divider" />
              <Link to="/login" className="mobile-link">
                Sign In
              </Link>
              <Link to="/register" className="mobile-link">
                Register
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
